import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { assessmentService } from '../../api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, User, Calendar, Award, AlertCircle } from 'lucide-react';

function ManualGrading() {
    const { submissionId } = useParams();
    const [submission, setSubmission] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [grades, setGrades] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const subRes = await assessmentService.getSubmissionResult(submissionId);
                setSubmission(subRes.data);

                const qRes = await assessmentService.getQuestions(subRes.data.assessment);
                const allQuestions = qRes.data;

                // Filter for open-ended questions that need manual grading
                const manualQuestions = allQuestions.filter(q => 
                    q.question_type === 'short_answer' || q.question_type === 'essay' || q.question_type === 'long_answer'
                );
                setQuestions(manualQuestions);

                // Initialize grades from existing answers
                const initialGrades = {};
                (subRes.data.answers || []).forEach(ans => {
                    if (ans.points_awarded !== undefined) {
                        initialGrades[ans.question] = {
                            points: ans.points_awarded,
                            feedback: ans.feedback || ''
                        };
                    }
                });
                setGrades(initialGrades);

            } catch (err) {
                toast.error('Failed to load submission for grading');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [submissionId]);

    const handleGradeChange = (questionId, field, value) => {
        setGrades(prev => ({
            ...prev,
            [questionId]: {
                ...prev[questionId],
                [field]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Submit grades - adjust endpoint as needed
            await assessmentService.submitManualGrades?.(submissionId, grades) ||
                await import('../../api').then(m => m.default.post(`/assessments/submissions/${submissionId}/grade/`, { grades }));
            
            toast.success('Grades saved successfully!');
            navigate(`/assessments/${submission.assessment}/result/${submissionId}`);
        } catch (err) {
            toast.error('Failed to save grades');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
                    <p className="text-muted">Loading submission...</p>
                </div>
            </DashboardLayout>
        );
    }

    if (!submission) {
        return (
            <DashboardLayout>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', minHeight: '50vh' }}>
                    <AlertCircle size={48} color="var(--color-error)" />
                    <h2>Submission not found</h2>
                    <Link to="/admin/submissions" className="btn btn-primary">Back to Submissions</Link>
                </div>
            </DashboardLayout>
        );
    }

    const getAnswer = (questionId) => {
        return submission.answers?.find(a => a.question === questionId);
    };

    return (
        <DashboardLayout>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                {/* Header */}
                <Link to={`/assessments/${submission.assessment}/result/${submissionId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)', textDecoration: 'none', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                    <ArrowLeft size={16} /> Back to Result
                </Link>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.75rem' }}>
                    <div style={{ width: '48px', height: '48px', background: 'var(--gradient-brand)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Award size={24} color="white" />
                    </div>
                    <div>
                        <h1 style={{ fontSize: '1.7rem', marginBottom: '0.2rem' }}>Manual Grading</h1>
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>{submission.assessment_title || 'Assessment'}</p>
                    </div>
                </div>

                {/* Submission Info */}
                <div style={{ background: 'var(--color-card-bg)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: '1.5rem', marginBottom: '1.75rem', boxShadow: 'var(--shadow-sm)',
                  color: 'var(--color-text-primary)' }}>
                    <h3 style={{ fontSize: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertCircle size={18} color="var(--color-primary)" /> Submission Information
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', fontSize: '0.9rem' }}>
                        <div>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                <User size={14} /> Student
                            </strong>
                            <span>{submission.student_name || submission.student_username || 'Unknown'}</span>
                        </div>
                        <div>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                <Award size={14} /> Current Score
                            </strong>
                            <span>{submission.score != null ? `${submission.score}%` : 'N/A'}</span>
                        </div>
                        <div>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                <Calendar size={14} /> Submitted
                            </strong>
                            <span>{submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : 'N/A'}</span>
                        </div>
                        <div>
                            <strong style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                                Status
                            </strong>
                            <span style={{
                                padding: '0.2rem 0.6rem', borderRadius: 'var(--radius-pill)', fontSize: '0.8rem', fontWeight: 600,
                                background: submission.is_passed ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                                color: submission.is_passed ? 'var(--color-success)' : 'var(--color-error)'
                            }}>
                                {submission.is_passed ? 'PASSED' : 'FAILED'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Grading Form */}
                <form onSubmit={handleSubmit}>
                    {questions.length === 0 ? (
                        <div style={{ background: 'var(--color-card-bg)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: '3rem', textAlign: 'center',
                  color: 'var(--color-text-primary)' }}>
                            <AlertCircle size={40} color="var(--color-text-muted)" style={{ marginBottom: '1rem' }} />
                            <h3 style={{ marginBottom: '0.5rem' }}>No manual grading needed</h3>
                            <p className="text-muted" style={{ fontSize: '0.9rem' }}>This assessment has no open-ended questions requiring manual grading.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {questions.map((question, index) => {
                                const answer = getAnswer(question.id);
                                const currentGrade = grades[question.id] || { points: '', feedback: '' };

                                return (
                                    <div key={question.id} style={{ background: 'var(--color-card-bg)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: '1.5rem', boxShadow: 'var(--shadow-sm)',
                  color: 'var(--color-text-primary)' }}>
                                        {/* Question Header */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--color-border)' }}>
                                            <div style={{ flex: 1 }}>
                                                <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>
                                                    Question {index + 1}
                                                </h3>
                                                <p style={{ fontSize: '1rem', lineHeight: 1.5 }}>{question.question_text}</p>
                                            </div>
                                            <span style={{ padding: '0.3rem 0.8rem', borderRadius: 'var(--radius-pill)', fontSize: '0.8rem', fontWeight: 600, background: 'rgba(47,124,123,0.1)', color: 'var(--color-primary)', whiteSpace: 'nowrap', marginLeft: '1rem' }}>
                                                {question.points} pts
                                            </span>
                                        </div>

                                        {/* Student Answer */}
                                        <div style={{ marginBottom: '1.25rem' }}>
                                            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                                                Student's Answer:
                                            </label>
                                            <div style={{ 
                                                padding: '1rem', 
                                                background: 'var(--color-bg-primary)', 
                                                borderRadius: 'var(--radius-md)', 
                                                border: '1px solid var(--color-border)',
                                                fontSize: '0.95rem',
                                                lineHeight: 1.6,
                                                minHeight: '60px',
                                                whiteSpace: 'pre-wrap',
                  color: 'var(--color-text-primary)'
                                            }}>
                                                {answer?.answer_text || <span className="text-muted" style={{ fontStyle: 'italic' }}>No answer provided</span>}
                                            </div>
                                        </div>

                                        {/* Grading Inputs */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                                                    Points Awarded *
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={question.points}
                                                    step="0.5"
                                                    required
                                                    value={currentGrade.points}
                                                    onChange={(e) => handleGradeChange(question.id, 'points', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.65rem',
                                                        borderRadius: 'var(--radius-md)',
                                                        border: '1px solid var(--color-border)',
                                                        fontSize: '0.95rem',
                                                        outline: 'none'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                    placeholder="0"
                                                />
                                                <small className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                                                    Max: {question.points}
                                                </small>
                                            </div>

                                            <div>
                                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                                                    Feedback (optional)
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={currentGrade.feedback}
                                                    onChange={(e) => handleGradeChange(question.id, 'feedback', e.target.value)}
                                                    placeholder="Provide feedback to the student..."
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.65rem',
                                                        borderRadius: 'var(--radius-md)',
                                                        border: '1px solid var(--color-border)',
                                                        fontSize: '0.9rem',
                                                        outline: 'none',
                                                        fontFamily: 'inherit',
                                                        resize: 'vertical'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {questions.length > 0 && (
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="btn btn-outline"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn btn-primary"
                                style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                            >
                                {saving ? 'Saving...' : <><Save size={18} /> Save Grades</>}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </DashboardLayout>
    );
}

export default ManualGrading;
